import { useMemo } from 'react'
import type { FareClassWithReward, FlightsClassTypes } from 'constants/flight'
import {
  CHARTER_CASH_CABINS,
  expandMaldivesCharterFareClassesWithRewards,
} from 'lib/maldivesCharter/charterCabins'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

/**
 * Canonical "what cabins should the Maldives charter fare-class modal show" list.
 *
 * Filters the caller's `baseAllowedFareClasses` down to {@link CHARTER_CASH_CABINS}
 * (drops legacy LUXROW/SkyLounge), then expands with reward-seat variants when the
 * `maldivesCharterRewardSeats` Optimizely flag is on.
 *
 * Both offer-page entry points (`CharterFlightFareClassSelect`, `FlashMobileStickyHeaderFilters`)
 * and Maldives Direct entry points (`MaldivesDirectSearchPage`, `MaldivesDirectSearchFilters`)
 * use this so every surface produces the same card set.
 */
export default function useMaldivesCharterModalFareClasses(
  baseAllowedFareClasses: ReadonlyArray<FlightsClassTypes>,
): ReadonlyArray<FareClassWithReward> {
  const isRewardSeatsEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterRewardSeats)
  return useMemo(() => {
    const cashOnly = baseAllowedFareClasses.filter(fc => CHARTER_CASH_CABINS.includes(fc))
    if (!isRewardSeatsEnabled) return cashOnly
    return expandMaldivesCharterFareClassesWithRewards(cashOnly)
  }, [baseAllowedFareClasses, isRewardSeatsEnabled])
}
